import React, { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faImage, 
  faCheck, 
  faTimes, 
  faDownload, 
  faSpinner,
  faArrowLeft,
  faEye,
  faEyeSlash
} from '@fortawesome/free-solid-svg-icons';
import { baseURL, baseURLnoapi, CURRENT_SEASON, AVAILABLE_SEASONS } from '../../Constants';
import { AuthContext } from '../../context/AuthContext';
import '../../styles/admin/admin-user-editor.scss';

interface FormData {
  steamID: string;
  playerName: string;
  name?: string;
  role?: string;
  team: {
    id: string;
    name: string;
  } | null;
  isAdmin: boolean;
  discordId?: string;
  discordUsername?: string;
}

interface Team {
  id: string;
  name: string;
}

interface PowerStatsAccount {
  steamID: string;
  name: string;
  playerName: string;
  role: string;
}

interface SetAdminStatusRequest {
  isAdmin: boolean;
  password?: string;
}

// Define a type for the user data returned from the API
interface UserData {
  steamID: string;
  playerName: string;
  name?: string;
  role?: string;
  team?: {
    id: string;
    name: string;
  } | null;
  isAdmin: boolean;
  discordId?: string;
  discordUsername?: string;
}

const AdminUserEditorPage: React.FC = () => {
  const { steamId } = useParams<{ steamId: string }>();
  const navigate = useNavigate();
  const isEditMode = !!steamId;
  
  // Get auth context
  const { token, isAdmin } = useContext(AuthContext);
  
  const [formData, setFormData] = useState<FormData>({
    steamID: '',
    playerName: '',
    name: '',
    role: '',
    team: null,
    isAdmin: false,
    discordId: '',    // Add this
    discordUsername: ''   // Add this 
  });
  
  // Track admin password separately
  const [adminPassword, setAdminPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [passwordValidationError, setPasswordValidationError] = useState<string | null>(null);
  
  const [teams, setTeams] = useState<Team[]>([]);
  const [selectedSeason, setSelectedSeason] = useState<number>(CURRENT_SEASON);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [imageUploading, setImageUploading] = useState(false);
  const [imageError, setImageError] = useState<string | null>(null);
  const [avatarUrl, setAvatarUrl] = useState<string>('');
  
  // PowerStats importer states
  const [importSteamId, setImportSteamId] = useState('');
  const [importSeason, setImportSeason] = useState('29'); // Default to season 29
  const [importLoading, setImportLoading] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);
  const [importSuccess, setImportSuccess] = useState(false);
  const [latestSeason, setLatestSeason] = useState<string | null>(null);
  
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Helper function to get auth headers
  const getAuthHeaders = () => {
    return {
      'Authorization': `Bearer ${token}`
    };
  };

  // Redirect if not an admin
  useEffect(() => {
    if (!isAdmin) {
      navigate('/admin/login');
    }
  }, [isAdmin, navigate]);

  useEffect(() => {
    if (isEditMode) {
      fetchUser();
      setImportSteamId(steamId || '');
    }

    fetchTeams(selectedSeason);
    fetchLatestSeason();

    // Set page title
    document.title = isEditMode
      ? 'Edit User - Admin Dashboard'
      : 'Create User - Admin Dashboard';
  }, [steamId]);

  // Fetch teams whenever season changes
  useEffect(() => {
    fetchTeams(selectedSeason);
  }, [selectedSeason]);

  const fetchLatestSeason = async () => {
    try {
      const response = await axios.get('https://api.powerstats.dk/PowerStats/api/Match/latestSeason');
      
      if (response.data) {
        setLatestSeason(response.data.toString());
        setImportSeason(response.data.toString());
      }
    } catch (err) {
      // Fallback to default season
    }
  };

  const fetchUser = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Ensure we have a token and steamId
      if (!token) {
        setError("Authentication token missing. Please log in again.");
        setLoading(false);
        return;
      }
      
      if (!steamId) {
        setError("User ID is missing. Please try again.");
        setLoading(false);
        return;
      }
      
      const response = await axios.get<UserData>(`${baseURL}/Account/${steamId}`, {
        headers: getAuthHeaders()
      });
      
      const userData = response.data;
      
      if (userData) {
setFormData({
  steamID: userData.steamID,
  playerName: userData.playerName || '',
  name: userData.name || '',
  role: userData.role || '',
  team: userData.team || null,
  isAdmin: userData.isAdmin || false,
  discordId: userData.discordId || '',
  discordUsername: userData.discordUsername || ''
});
        
        // If user is already admin, reset password field (we can't see the current password)
        if (userData.isAdmin) {
          setAdminPassword('');
        }
        
        // Check for avatar
        await checkAvatar(userData.steamID);
      }
    } catch (err: any) {
      setError('Failed to load user data. ' + (err.response?.data || err.message));
    } finally {
      setLoading(false);
    }
  };
  
  // Simplified avatar checking function
  const checkAvatar = async (steamID: string) => {
    if (!steamID) return;
    
    // First check if we have a local avatar
    const serverAvatarUrl = `${baseURLnoapi}/images/players/${steamID}.png`;
    
    try {
      // Try a HEAD request to check if local avatar exists
      const headResult = await axios.head(serverAvatarUrl);
      if (headResult.status === 200) {
        setAvatarUrl(serverAvatarUrl);
      }
    } catch (err) {
      // If no local avatar, don't set any URL - it will show the placeholder
      setAvatarUrl('');
    }
  };
  
  const fetchTeams = async (season: number = CURRENT_SEASON) => {
    try {
      const response = await axios.get<Team[]>(`${baseURL}/Team/season/${season}`, {
        headers: token ? getAuthHeaders() : {}
      });

      setTeams(response.data);
    } catch (err) {
      // No need to set error since it's not critical
      setTeams([]);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    if (name === 'isAdmin') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({
        ...prev,
        isAdmin: checked
      }));
      
      // Reset password validation error when toggling admin
      setPasswordValidationError(null);
    } else if (name === 'teamId') {
      if (value === '') {
        setFormData(prev => ({
          ...prev,
          team: null
        }));
      } else {
        const selectedTeam = teams.find(team => team.id === value);
        if (selectedTeam) {
          setFormData(prev => ({
            ...prev,
            team: {
              id: selectedTeam.id,
              name: selectedTeam.name
            }
          }));
        }
      }
    } else if (name === 'adminPassword') {
      setAdminPassword(value);
      setPasswordValidationError(null);
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const validateAdminPassword = (): boolean => {
    // If admin is not selected, no need to validate password
    if (!formData.isAdmin) return true;
    
    // In edit mode, if the user was already an admin, they don't need to provide a password
    if (isEditMode && formData.isAdmin && adminPassword === '') return true;
    
    // If admin is selected, password is required and should be at least 8 characters
    if (adminPassword.length < 8) {
      setPasswordValidationError('Password must be at least 8 characters long');
      return false;
    }
    
    return true;
  };

  // Simplified PowerStats import function
  const importFromPowerStats = async () => {
    if (!importSteamId) {
      setImportError('Please enter a Steam ID to import.');
      return;
    }
  
    setImportLoading(true);
    setImportError(null);
    setImportSuccess(false);
  
    try {
      // Check if user already exists
      let existingUser: UserData | null = null;
      let userExists = false;
      
      try {
        const userResponse = await axios.get<UserData>(
          `${baseURL}/Account/${importSteamId}`,
          { headers: getAuthHeaders() }
        );
        
        if (userResponse.data) {
          existingUser = userResponse.data;
          userExists = true;
        }
      } catch (err) {
        // User doesn't exist, which is fine for this flow
        userExists = false;
      }
      
      // Get player data from PowerStats
      const importUrl = `https://api.powerstats.dk/PowerStats/api/PlayerStat/getPlayerBySeasonAndSteamId?steamId=${importSteamId}&season=${importSeason}`;
      
      const response = await axios.get<{ account: PowerStatsAccount }>(importUrl);
  
      if (response.data && response.data.account) {
        const playerData = response.data.account;
        
        // Update form data with imported values
        setFormData(prev => ({
          ...prev,
          steamID: playerData.steamID,
          playerName: playerData.playerName || prev.playerName,
          name: playerData.name || prev.name,
          role: playerData.role || prev.role,
          // Keep existing values for team and admin status if user exists
          team: existingUser ? existingUser.team || null : prev.team,
          isAdmin: existingUser ? existingUser.isAdmin || false : prev.isAdmin
        }));
  
        // Import avatar using backend endpoint
        await importAvatar(playerData.steamID);
  
        setImportSuccess(true);
        
        // If user exists, let the user know they're updating an existing account
        if (userExists && !isEditMode) {
          setImportError('Note: This account already exists in the system. You will update the existing account when you save.');
        }
        
        // Auto-clear success message after 3 seconds
        setTimeout(() => {
          setImportSuccess(false);
        }, 3000);
      } else {
        setImportError('No player data found for this Steam ID and season.');
      }
    } catch (err: any) {
      setImportError('Failed to import from PowerStats. ' + (err.response?.data || err.message));
    } finally {
      setImportLoading(false);
    }
  };

  // New function to import avatar using backend endpoint
  const importAvatar = async (steamID: string) => {
    if (!steamID || !token) return false;
    
    setImageUploading(true);
    setImageError(null);
    
    try {
      // Call backend endpoint to import avatar
      const response = await axios.get<{ avatarUrl: string; source?: string }>(
        `${baseURL}/Account/${steamID}/import-avatar`,
        { headers: getAuthHeaders() }
      );
      
      if (response.data && response.data.avatarUrl) {
        // Set avatar URL from response
        const newAvatarUrl = response.data.avatarUrl.startsWith('http') 
          ? response.data.avatarUrl 
          : `${baseURLnoapi}${response.data.avatarUrl}`;
        
        // Add timestamp to avoid caching issues
        setAvatarUrl(`${newAvatarUrl}?t=${new Date().getTime()}`);
        
        return true;
      }
      
      return false;
    } catch (err: any) {
      setImageError('Failed to import avatar: ' + (err.response?.data || err.message));
      return false;
    } finally {
      setImageUploading(false);
    }
  };

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!token) {
      setImageError('You need to be logged in as an admin to upload avatars.');
      return;
    }
    
    if (!formData.steamID) {
      setImageError('Please enter a Steam ID first.');
      return;
    }
    
    const file = e.target.files?.[0];
    if (!file) {
      return;
    }

    // Basic validation
    if (file.type !== 'image/png') {
      setImageError('Invalid file type. Only PNG files are allowed.');
      return;
    }

    setImageUploading(true);
    setImageError(null);

    try {
      const formDataObj = new FormData();
      formDataObj.append('file', file);

      // Make API call to upload the avatar
      const uploadUrl = `${baseURL}/Account/${formData.steamID}/avatar`;
      
      const response = await axios.post(
        uploadUrl,
        formDataObj,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      // After successful upload, update avatar URL to point to our server
      const newAvatarUrl = `${baseURLnoapi}/images/players/${formData.steamID}.png`;
      
      // Add cache buster to force browser to reload the image
      setAvatarUrl(`${newAvatarUrl}?t=${new Date().getTime()}`);
      
    } catch (err: any) {
      setImageError('Failed to upload avatar: ' + (err.response?.data || err.message));
    } finally {
      setImageUploading(false);
      // Reset the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };
  
  const handleDeleteAvatar = async () => {
    if (!token || !formData.steamID) {
      return;
    }
    
    setImageUploading(true);
    setImageError(null);
    
    try {
      const deleteUrl = `${baseURL}/Account/${formData.steamID}/avatar`;
      
      await axios.delete(deleteUrl, {
        headers: getAuthHeaders()
      });
      
      setAvatarUrl('');
    } catch (err: any) {
      setImageError('Failed to delete avatar: ' + (err.response?.data || err.message));
    } finally {
      setImageUploading(false);
    }
  };

  // Function to set admin status and password
  const setAdminStatus = async (steamID: string, isAdmin: boolean, password?: string) => {
    try {
      const setAdminUrl = `${baseURL}/Admin/setAdminStatus/${steamID}`;
      const requestData: SetAdminStatusRequest = {
        isAdmin: isAdmin
      };
      
      // Only include password if provided and setting to admin
      if (isAdmin && password) {
        requestData.password = password;
      }
      
      await axios.post(
        setAdminUrl,
        requestData,
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      return true;
    } catch (err: any) {
      // Add error message for admin status update failure
      let errorMessage = 'Failed to set admin status. ';
      if (err.response?.data) {
        errorMessage += typeof err.response.data === 'string' 
          ? err.response.data 
          : JSON.stringify(err.response.data);
      }
      
      setError(prev => prev ? `${prev} ${errorMessage}` : errorMessage);
      return false;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate admin password if needed
    if (!validateAdminPassword()) {
      return;
    }
    
    setSaving(true);
    setError(null);
    
    try {
      // Prepare user data
      const userData = {
        ...formData,
        // Ensure clean data without extra whitespace
        steamID: formData.steamID.trim(),
        playerName: formData.playerName.trim(),
        name: formData.name?.trim() || null,
        role: formData.role?.trim() || null,
discordId: formData.discordId?.toString() || '',
discordUsername: formData.discordUsername?.toString() || ''
        
      };
      
      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      };
      
      // First check if user already exists (only needed in create mode)
      let userExists = false;
      
      if (!isEditMode) {
        try {
          const checkResponse = await axios.get(
            `${baseURL}/Account/${userData.steamID}`,
            { headers: getAuthHeaders() }
          );
          
          userExists = !!checkResponse.data;
        } catch (err) {
          // If we get an error, assume user doesn't exist
          userExists = false;
        }
      } else {
        // In edit mode, we know the user exists
        userExists = true;
      }
      
      let response;
      
      // Now save the user data based on whether the user exists
      if (userExists) {
        // Use PUT to update the existing user
        const putUrl = `${baseURL}/Account/${userData.steamID}`;
        response = await axios.put(putUrl, userData, { headers });
      } else {
        // Use POST to create a new user
        const postUrl = `${baseURL}/Account`;
        response = await axios.post(postUrl, userData, { headers });
      }
      
      // Handle admin status setting
      if (userData.isAdmin) {
        // Add a small delay to ensure the user is fully created in the database
        await new Promise(resolve => setTimeout(resolve, 500));
        
        const adminResult = await setAdminStatus(
          userData.steamID, 
          userData.isAdmin, 
          adminPassword || undefined
        );
        
        if (!adminResult) {
          setError('User was saved but admin privileges could not be set. Please edit the user to try again.');
          setSaving(false);
          return;
        }
      }
      
      // Redirect to users management page
      navigate('/admin/users');
    } catch (err: any) {
      console.error('Error saving user:', err);
      
      let errorMessage = 'Failed to save user. Please try again.';
      
      // Try to extract more detailed error information
      if (err.response?.data) {
        errorMessage = typeof err.response.data === 'string' 
          ? err.response.data 
          : JSON.stringify(err.response.data);
      }
      
      setError(errorMessage);
      setSaving(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  if (loading) {
    return (
      <div className="admin-user-editor loading">
        <div className="container">
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-user-editor">
      <div className="container">
        <div className="editor-header">
          <button 
            type="button"
            className="back-button"
            onClick={() => navigate('/admin/users')}
          >
            <FontAwesomeIcon icon={faArrowLeft} /> Back to Users
          </button>
          <h1>{isEditMode ? 'Edit User' : 'Create New User'}</h1>
        </div>
        
        {error && (
          <div className="alert alert-danger">
            {error}
          </div>
        )}
        
        <form onSubmit={handleSubmit}>
          <div className="form-grid">
            <div className="form-main">
              <div className="form-group">
                <label htmlFor="steamID">Steam ID</label>
                <input
                  type="text"
                  id="steamID"
                  name="steamID"
                  value={formData.steamID}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter Steam ID"
                  disabled={isEditMode} // Can't edit Steam ID in edit mode
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="playerName">Player Name</label>
                <input
                  type="text"
                  id="playerName"
                  name="playerName"
                  value={formData.playerName}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter player name"
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="name">Name</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name || ''}
                  onChange={handleInputChange}
                  placeholder="Enter real name (optional)"
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="season">Season (for team filter)</label>
                <select
                  id="season"
                  name="season"
                  value={selectedSeason}
                  onChange={(e) => setSelectedSeason(Number(e.target.value))}
                >
                  {AVAILABLE_SEASONS.map(season => (
                    <option key={season} value={season}>
                      Season {season}
                    </option>
                  ))}
                </select>
                <div className="help-text">
                  Select which season's teams to display in the team dropdown below.
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="teamId">Team</label>
                <select
                  id="teamId"
                  name="teamId"
                  value={formData.team?.id || ''}
                  onChange={handleInputChange}
                >
                  <option value="">No Team</option>
                  {teams.map(team => (
                    <option key={team.id} value={team.id}>
                      {team.name}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="form-group">
                <label htmlFor="role">Role</label>
                <input
                  type="text"
                  id="role"
                  name="role"
                  value={formData.role || ''}
                  onChange={handleInputChange}
                  placeholder="Enter role (e.g. IGL, Rifler, AWPer)"
                />
              </div>
              
              <div className="form-group checkbox-group">
                <label htmlFor="isAdmin" className="checkbox-label">
                  <input
                    type="checkbox"
                    id="isAdmin"
                    name="isAdmin"
                    checked={formData.isAdmin}
                    onChange={handleInputChange}
                  />
                  <span>Administrator</span>
                </label>
                <div className="help-text">
                  Administrators have full access to the admin dashboard and all management features.
                </div>
              </div>
              
              {/* Admin Password Field - only show when isAdmin is checked */}
              {formData.isAdmin && (
                <div className="form-group">
                  <label htmlFor="adminPassword">
                    Admin Password {isEditMode ? '(Set new password)' : ''}
                  </label>
                  <div className="password-input-container">
                    <input
                      type={showPassword ? "text" : "password"}
                      id="adminPassword"
                      name="adminPassword"
                      value={adminPassword}
                      onChange={handleInputChange}
                      placeholder={isEditMode ? "Enter new password (leave empty to keep current)" : "Enter admin password"}
                      className={passwordValidationError ? "input-error" : ""}
                    />
                    <button 
                      type="button"
                      className="password-toggle-btn"
                      onClick={togglePasswordVisibility}
                      tabIndex={-1}
                    >
                      <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                    </button>
                  </div>
                  {passwordValidationError && (
                    <div className="password-error">
                      {passwordValidationError}
                    </div>
                  )}
                  <div className="help-text">
                    {isEditMode 
                      ? "Leave empty to keep the current password." 
                      : "Password must be at least 8 characters long."}
                  </div>
                </div>
              )}
            </div>
            
            <div className="form-sidebar">
              <div className="sidebar-section">
                <h3>Import from PowerStats</h3>
                <div className="importer-container">
                  <div className="form-group">
                    <label htmlFor="importSteamId">Steam ID</label>
                    <div className="import-input-group">
                      <input
                        type="text"
                        id="importSteamId"
                        value={importSteamId}
                        onChange={(e) => setImportSteamId(e.target.value)}
                        placeholder="Enter Steam ID to import"
                      />
                    </div>
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="importSeason">Season</label>
                    <input
                      type="text"
                      id="importSeason"
                      value={importSeason}
                      onChange={(e) => setImportSeason(e.target.value)}
                      placeholder="Season number"
                    />
                    <div className="help-text">
                      {latestSeason ? `Latest season: ${latestSeason}` : ''}
                    </div>
                  </div>
                  
                  <button
                    type="button"
                    className="btn-import"
                    onClick={importFromPowerStats}
                    disabled={importLoading || !importSteamId}
                  >
                    {importLoading ? (
                      <>
                        <FontAwesomeIcon icon={faSpinner} spin /> Importing...
                      </>
                    ) : (
                      <>
                        <FontAwesomeIcon icon={faDownload} /> Import Data
                      </>
                    )}
                  </button>
                  
                  {importError && (
                    <div className="import-error">
                      <FontAwesomeIcon icon={faTimes} />
                      {importError}
                    </div>
                  )}
                  
                  {importSuccess && (
                    <div className="import-success">
                      <FontAwesomeIcon icon={faCheck} />
                      Player data imported successfully!
                    </div>
                  )}
                  
                  <div className="help-text import-note">
                    Imports player data from PowerStats including player name, real name, and role.
                  </div>
                </div>
              </div>
              
              <div className="sidebar-section">
                <h3>User Avatar</h3>
                <div className="avatar-preview-container">
                  {avatarUrl ? (
                    <div className="avatar-preview">
                      <img 
                        src={avatarUrl} 
                        alt="User avatar" 
                        onError={() => {
                          setAvatarUrl('');
                          setImageError('Could not load avatar.');
                        }}
                      />
                    </div>
                  ) : (
                    <div className="avatar-placeholder">
                      <FontAwesomeIcon icon={faImage} />
                      <span>No Avatar</span>
                    </div>
                  )}
                </div>
                
                <div className="avatar-actions">
                  <div className="upload-container">
                    <input
                      type="file"
                      ref={fileInputRef}
                      accept="image/png"
                      onChange={handleAvatarUpload}
                      style={{ display: 'none' }}
                    />
                    <button
                      type="button"
                      className="btn-outline"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={imageUploading || !formData.steamID}
                    >
                      {avatarUrl ? 'Change Avatar' : 'Upload Avatar'}
                    </button>
                    
                    <button
                      type="button"
                      className="btn-outline"
                      onClick={() => importAvatar(formData.steamID)}
                      disabled={imageUploading || !formData.steamID}
                      title="Import avatar from PowerStats/DPL"
                    >
                      <FontAwesomeIcon icon={faDownload} />
                      Import Avatar
                    </button>
                    
                    {avatarUrl && (
                      <button
                        type="button"
                        className="btn-outline btn-danger"
                        onClick={handleDeleteAvatar}
                        disabled={imageUploading}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                  
                  {imageUploading && <div className="loading-spinner small"></div>}
                  
                  {imageError && (
                    <div className="avatar-error">
                      {imageError}
                    </div>
                  )}
                  
                  <div className="avatar-note">
                    Only PNG images are accepted. Recommended: Portrait orientation (400x600px) showing full face to chest with space above head. You can also import avatar from PowerStats.
                  </div>
                </div>
              </div>
              
              <div className="sidebar-section actions">
                <div className="action-buttons">
                  <button 
                    type="button" 
                    className="btn-secondary"
                    onClick={() => navigate('/admin/users')}
                  >
                    CANCEL
                  </button>
                  <button 
                    type="submit" 
                    className="btn-primary"
                    disabled={saving}
                  >
                    {saving ? 'SAVING...' : isEditMode ? 'UPDATE USER' : 'CREATE USER'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminUserEditorPage;